import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { EnTipoEnvio, ParticipanteExternoAtivo } from './chat-export.types';
import { ChatService } from '../chat.service';
import { SignalrService } from 'app/signalr.service';
import { ChatExportService } from './chat-export.service';

@Component({
  selector: 'chat-export',
  templateUrl: './chat-export.component.html',
  styleUrls: ['./chat-export.component.css']
})
export class ChatExportComponent implements OnInit, OnDestroy, OnChanges {

  @Input() dadosConversa: any[] = [];
  @Input() chatFinalizado: boolean = false;
  @Input() catalogo: string = '';
  @Input() mensagemGuid: string = '';

  dadosConversaExportados: string[] = [];

  menuAberto = false;
  whatsappHabilitado = false;
  participanteExternoAtivo: ParticipanteExternoAtivo | null = null;
  canalSelecionado: EnTipoEnvio | null = null;
  contato = '';
  contatoValido = false;
  uploadConcluido = false;
  mensagemFeedback = '';
  feedbackErro = false;
  private feedbackTimeout: number | null = null;
  private validacaoParticipanteIniciada = false;

  constructor(
    private _signalR: SignalrService,
    private _chatService: ChatService,
    private _chatExportService: ChatExportService
  ) { }
  
  ngOnInit(): void {
    
  }

  ngOnDestroy(): void {
    
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatFinalizado'] && this.chatFinalizado && !this.validacaoParticipanteIniciada) {
      this.validacaoParticipanteIniciada = true;
      this.validarParticipanteExterno();
    }
  }

  private validarParticipanteExterno(): void {
    const token = this._signalR.token;

    if (!this.mensagemGuid || !this.catalogo || !token) {
      console.error('Não foi possível validar o participante externo: dados de autenticação ou da conversa ausentes.');
      return;
    }

    this._chatExportService.validateParticipanteExterno(
      this._chatService.chatApiUrl,
      this.mensagemGuid,
      this.catalogo,
      token
    ).subscribe({
      next: (participante: ParticipanteExternoAtivo) => {
        if (!participante || participante.participanteId <= 0) {
          this.participanteExternoAtivo = null;
          this.whatsappHabilitado = false;
          return;
        }

        this.participanteExternoAtivo = participante;
        this.whatsappHabilitado = true;
      },
      error: (error: any) => {
        this.participanteExternoAtivo = null;
        this.whatsappHabilitado = false;
        console.error('Erro ao validar participante externo para envio pelo WhatsApp:', error);
      }
    });
  }

  alternarMenu(): void {
    this.menuAberto = !this.menuAberto;
    this.canalSelecionado = null;
    this.contato = '';
    this.contatoValido = false;
    this.uploadConcluido = false;
    this.mensagemFeedback = '';
  }

  selecionarCanal(canal: EnTipoEnvio): void {
    if (canal === EnTipoEnvio.WhatsApp && (!this.whatsappHabilitado || !this.participanteExternoAtivo)) {
      return;
    }

    this.canalSelecionado = canal;
    this.contato = '';
    this.contatoValido = canal === EnTipoEnvio.WhatsApp;
    this.uploadConcluido = false;

    if (canal === EnTipoEnvio.WhatsApp) {
      this.menuAberto = false;
      this.confirmarEnvio();
    }
  }

  atualizarContato(event: Event): void {
    const campo = event.target as HTMLInputElement;
    this.contato = campo.value;
    this.contatoValido = this.canalSelecionado === EnTipoEnvio.Email
      ? campo.value.trim().length > 0 && campo.validity.valid
      : this.canalSelecionado === EnTipoEnvio.WhatsApp && campo.value.trim().length > 0;
    this.uploadConcluido = false;
  }

  confirmarEnvio(): void {
    if (this.contatoValido) {
      this.mensagemFeedback = '';
      this.feedbackErro = false;

      const mensagens = Array.isArray(this.dadosConversa)
        ? this.dadosConversa.filter(mensagem => mensagem != null)
        : [];

      if (mensagens.length === 0) {
        this.mostrarFeedbackErro('Não há mensagens nesta conversa para enviar.');
        return;
      }

      const participanteEndereco = this.participanteExternoAtivo?.participanteEndereco?.trim();
      if (this.canalSelecionado === EnTipoEnvio.WhatsApp && !participanteEndereco) {
        this.mostrarFeedbackErro('Não foi possível identificar o WhatsApp vinculado a esta sessão.');
        return;
      }

      const arquivo = this.criarArquivoConversa(mensagens);
      const canal = this.canalSelecionado!;
      const destino = canal === EnTipoEnvio.WhatsApp
        ? participanteEndereco || ''
        : this.contato;
      const participanteExternoAtivo = canal === EnTipoEnvio.WhatsApp
        ? this.participanteExternoAtivo!
        : {};

      this._chatExportService.postExport(
        this._chatService.chatApiUrl,
        this._signalR.chaveSignalR!,
        arquivo,
        canal,
        destino,
        participanteExternoAtivo
      ).subscribe(
        (response: any) => {
          this.uploadConcluido = true;
          const mensagem = canal === EnTipoEnvio.Email
            ? `Pronto! O arquivo da conversa foi enviado para ${destino}. Em breve você o receberá por e-mail.`
            : 'Pronto! A conversa foi enviada para o WhatsApp vinculado a esta sessão. Em breve você receberá o arquivo por lá.';
          this.mostrarFeedback(mensagem, false, 5000);
          this.menuAberto = false;
        },
        (error: any) => {
          console.error('Erro ao enviar arquivo:', error);
          this.mostrarFeedbackErro('Não foi possível enviar o arquivo. Tente novamente.');
        }
      );
    }
  }

  private mostrarFeedbackErro(mensagem: string): void {
    this.mostrarFeedback(mensagem, true, 3000);
  }

  private mostrarFeedback(mensagem: string, erro: boolean, duracao: number): void {
    if (this.feedbackTimeout !== null) {
      window.clearTimeout(this.feedbackTimeout);
    }

    this.mensagemFeedback = mensagem;
    this.feedbackErro = erro;
    this.feedbackTimeout = window.setTimeout(() => {
      this.mensagemFeedback = '';
      this.feedbackErro = false;
      this.feedbackTimeout = null;
    }, duracao);
  }

  downloadConversa(): void {
    this.mensagemFeedback = '';
    this.feedbackErro = false;

    const mensagens = Array.isArray(this.dadosConversa)
      ? this.dadosConversa.filter(mensagem => mensagem != null)
      : [];

    if (mensagens.length === 0) {
      this.mostrarFeedbackErro('Não há mensagens nesta conversa para baixar.');
      this.menuAberto = false;
      return;
    }

    try {
      const arquivo = this.criarArquivoConversa(mensagens);
      const url = window.URL.createObjectURL(arquivo);
      const a = document.createElement('a');
      a.href = url;
      a.download = arquivo.name;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      this.menuAberto = false;
    } catch (error) {
      this.mostrarFeedbackErro('Não foi possível gerar o arquivo. Tente novamente.');
      this.menuAberto = false;
    }
  }

  private criarArquivoConversa(mensagens: any[]): File {
    this.montarConteudoMensagem(mensagens);
    const agora = new Date();
    const conteudo = [
      `Conversa exportada em ${agora.toLocaleString('pt-BR')}`,
      '',
      ...this.dadosConversaExportados
    ].join('\n');
    const nomeArquivo = `conversa_inteliax_${agora.toLocaleDateString('sv-SE')}.txt`;
    const arquivo: any = new File([conteudo], nomeArquivo, { type: 'text/plain;charset=utf-8' });
    arquivo.legenda = '';
    return arquivo;
  }

  montarConteudoMensagem(mensagens: any[]): void {
    this.dadosConversaExportados = [];
    mensagens.forEach((mensagem) => {
      const nomeContato = mensagem?.from?.name
        || (mensagem?.type === 'evento' ? 'Sistema' : mensagem?.type === 'sent' ? 'Você' : 'Participante');
      const conteudo = this.tratarConteudoMensagem(mensagem);
      const data = mensagem?.date ? new Date(mensagem.date) : null;
      const dataHora = data && !isNaN(data.getTime()) ? data.toLocaleString('pt-BR') : 'Sem data';

      this.dadosConversaExportados.push(`[${dataHora}] ${nomeContato}: ${conteudo}`);
    });
  }

  tratarConteudoMensagem(mensagem: any): string {
    if (mensagem.anexo) {
      const nome = mensagem?.anexoConteudo?.name || 'Arquivo anexado';
      const nomeArquivo = nome.split(/[\\/]/).pop();
      const urlArquivo = mensagem?.anexoConteudo?.urlArquivo;
      return JSON.stringify({
        Caminho: nomeArquivo,
        Url: urlArquivo || ''
      });
    }
    else if (mensagem?.botoes != null && Array.isArray(mensagem.botoes) && mensagem.botoes.length > 0) {
      const botoesLink = mensagem.botoes.filter((b: any) => b.tipo === 'link');
      if (botoesLink.length > 0) {
        return JSON.stringify(
          mensagem.botoes.filter((b: any) => b.tipo == 'link').map((botao: any) => {
            return { Url: botao.url || '' };
          })
        );
      }
    }
    return mensagem?.text || '';
  }

  @HostListener('document:keydown.escape')
  fecharMenu(): void {
    this.menuAberto = false;
  }
}
